<template>
  <div class="currency-modal critical-component-clear">
    <h3
      class="currency-modal__header"
      v-text="$string('currency.choose_country_title')"
    />

    <p
      class="currency-modal__desc"
      v-text="$string('currency.description_above_dropdown')"
    />

    <div class="currency-modal__dropdown">
      <label
        for="currency-modal-select"
        class="currency-modal__flag"
      >
        <responsive-image
          :image="{
            src: getFlag(selectedCountry.code),
            alt: selectedCountry.name + ' flag image'
          }"
          :alt="`${selectedCountry.name} flag image`"
          class="currency-modal__flag-img"
          sizes="20px"
        />
      </label>
      <select
        id="currency-modal-select"
        v-model="dropdownCountry"
        class="currency-modal__select"
      >
        <option
          v-for="country of localization.countries"
          :key="country.code"
          :value="country.code"
          v-text="country.name.toUpperCase()"
        />
      </select>
      <label
        for="currency-modal-select"
        class="currency-modal__select-arrow"
      >
        <icon-arrow-down-filled />
      </label>
    </div>
    <p
      class="currency-modal__desc"
      v-html="descHtml"
    />

    <div>
      <button
        :key="selectedCountry.code"
        type="submit"
        class="currency-modal__submit button button--primary"
        :disabled="selectedCountry.code === activeCountry.code"
        v-text="$string('currency.submit')"
      />
    </div>

    <input
      v-model="selectedCountry.code"
      type="hidden"
      name="country_code"
    >
  </div>
</template>

<script>

/**
 * Vue: Currency modal (currency-modal)
 * -----------------------------------------------------------------------------
 * Modal prompt to allow users to select currency.
 *
 * @param {Number} liquid.cookieDismiss - Number of days to save cookie.
 * @param {Object} localization - Shopify localization object.
 *
 */
import { nextTick } from 'vue'
import ResponsiveImage from '~global/images/responsive-image'
import IconArrowDownFilled from '~icons/direction/arrow/down-filled.svg'

export default {
  name: 'CurrencyModal',

  components: {
    IconArrowDownFilled,
    ResponsiveImage,
  },

  props: {
    localization: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },

  data() {
    return {
      dropdownCountry: this.localization.current.code,
      selectedCountry: this.localization.current,
    }
  },

  computed: {

    /**
     * Compute header text.
     * @returns {String}
     */
    headerText() {
      return this.$string('currency.title')
    },

    /**
     * Compute description text.
     * @returns {String}
     */
    descHtml() {
      const bp1 = this.$string('currency.description_below_dropdown_bullet_point_1').replace(
        '{{ currency }}',
        `${this.activeCountry.currency} (${this.activeCountry.symbol})`,
      )
      const bp2 = this.$string('currency.description_below_dropdown_bullet_point_2').replace(
        '{{ currency }}',
        `${this.activeCountry.currency} (${this.activeCountry.symbol})`,
      )
      const bp3 = this.$string('currency.description_below_dropdown_bullet_point_3').replace(
        '{{ currency }}',
        `${this.activeCountry.currency} (${this.activeCountry.symbol})`,
      )
      const bp4 = this.$string('currency.description_below_dropdown_bullet_point_4').replace(
        '{{ currency }}',
        `${this.activeCountry.currency} (${this.activeCountry.symbol})`,
      )
      return bp1?.length || bp2?.length || bp3?.length
        ? `
        <ul>
          ${bp1?.length ? `<li>${bp1}</li>` : ''}
          ${bp2?.length ? `<li>${bp2}</li>` : ''}
          ${bp3?.length ? `<li>${bp3}</li>` : ''}
          ${bp4?.length ? `<li>${bp4}</li>` : ''}
        </ul>
      `
        : ''
    },

    /**
   * Compute active country.
   * @returns {Object}
   */
    activeCountry() {
      return this.localization.current
    },
  },

  watch: {
    dropdownCountry(newDropdownCountry) {
      this.selectedCountry = this.localization.countries.find(
        country => country.code === newDropdownCountry,
      ) || this.localization.current
    },
    async 'selectedCountry.code'(newCode) {
      if (newCode !== this.activeCountry.code) {
        await this.$nextTick()
        if (window.cnvs?.EventBus) {
          window.cnvs.EventBus.emit('overlay:update', 'currencyModal')
        }
      }
    },
  },

  methods: {

    /**
     * Set country based on user selection, submit form.
     * @param {Object} country - Country object.
     * @param {Event} event - Click event.
     */
    setCountry(country, event) {
      const form = event.target.closest('form')

      this.selectedCountry = country.code

      nextTick().then(() => {
        form.submit()
      })
    },

    /**
     * Get flag image.
     * @param {String} code - country code.
     * @returns {String}
     */
    getFlag(code) {
      return `https://cdn.shopify.com/static/images/flags/${code.toLowerCase()}.svg`
    },

    /**
     * Convert from store base currency to requested one
     * @param {Number} amount - Amount to convert
     * @param {String} currencyCode - Currency iso_code to convert to
     * @returns {Number} Converted amount
     */
    convertAmountToCurrency(amount, currencyCode) {
      return window.Currency?.convert(
        100,
        'USD',
        currencyCode,
      ) || amount
    },
  },
}
</script>

<style lang="scss">
@import './currency-modal';
</style>
